import { Injectable, signal } from '@angular/core';

import { Language } from './translation.types';
import { de } from './translations/de';
import { en, TranslationKey } from './translations/en';

const STORAGE_KEY = 'farmfieldplanner.language';

const translations = {
  de,
  en,
};

const cropKeys: Record<string, TranslationKey> = {
  wheat: 'crop.wheat',
  barley: 'crop.barley',
  canola: 'crop.canola',
  oat: 'crop.oat',

  maize: 'crop.maize',
  corn: 'crop.maize',

  sunflower: 'crop.sunflower',
  soybean: 'crop.soybean',
  potato: 'crop.potato',

  rice: 'crop.rice',
  ricelonggrain: 'crop.rice_long_grain',
  rice_long_grain: 'crop.rice_long_grain',

  sugarbeet: 'crop.sugar_beet',
  sugar_beet: 'crop.sugar_beet',

  sugarcane: 'crop.sugar_cane',
  sugar_cane: 'crop.sugar_cane',

  cotton: 'crop.cotton',
  sorghum: 'crop.sorghum',
  grape: 'crop.grape',
  olive: 'crop.olive',
  poplar: 'crop.poplar',
  beetroot: 'crop.beetroot',
  carrot: 'crop.carrot',
  parsnip: 'crop.parsnip',

  greenbean: 'crop.green_bean',
  green_bean: 'crop.green_bean',

  pea: 'crop.pea',
  spinach: 'crop.spinach',
  grass: 'crop.grass',

  oilseedradish: 'crop.oilseed_radish',
  oilseed_radish: 'crop.oilseed_radish',

  rye: 'crop.rye',
  millet: 'crop.millet',
  poppy: 'crop.poppy',
  spelt: 'crop.spelt',
  hops: 'crop.hops',
  lavender: 'crop.lavender',
  alfalfa: 'crop.alfalfa',
  clover: 'crop.clover',
  tobacco: 'crop.tobacco',
  hemp: 'crop.hemp',

  redcabbage: 'crop.red_cabbage',
  red_cabbage: 'crop.red_cabbage',

  whitecabbage: 'crop.white_cabbage',
  white_cabbage: 'crop.white_cabbage',

  meadow: 'crop.meadow',
  onion: 'crop.onion',
};

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  readonly language = signal<Language>(
    this.detectLanguage(),
  );

  constructor() {
    if (typeof document !== 'undefined') {
      document.documentElement.lang =
        this.language();
    }
  }

  setLanguage(language: Language): void {
    this.language.set(language);

    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(
        STORAGE_KEY,
        language,
      );
    }

    if (typeof document !== 'undefined') {
      document.documentElement.lang =
        language;
    }
  }

  t(key: TranslationKey): string {
    return translations[this.language()][key];
  }

  cropLabel(code: string): string {
    const normalizedCode =
      code.trim().toLowerCase();

    if (normalizedCode === 'unknown') {
      return this.t('crop.none');
    }

    const key = cropKeys[normalizedCode];

    return key
      ? this.t(key)
      : code;
  }

  private detectLanguage(): Language {
    if (typeof localStorage !== 'undefined') {
      const storedLanguage =
        localStorage.getItem(STORAGE_KEY);

      if (
        storedLanguage === 'de' ||
        storedLanguage === 'en'
      ) {
        return storedLanguage;
      }
    }

    if (
      typeof navigator !== 'undefined' &&
      navigator.language
        .toLowerCase()
        .startsWith('de')
    ) {
      return 'de';
    }

    return 'en';
  }
}
